import {
  BrowserRouter,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import UserListPage from "./modules/users/pages/UserListPage";
import UserDetailPage from "./modules/users/pages/UserDetailPage";
import RoleListPage from "./modules/roles/pages/RoleListPage";
import RoleDetailPage from "./modules/roles/pages/RoleDetailPage";
import CreateRolePage from "./modules/roles/pages/CreateRolePage";
import DropdownManagerPage from "./modules/dropdowns/pages/DropdownManagerPage";
import ModuleManagerPage from "./modules/module-manager/pages/ModuleManagerPage";
import AuditCenterPage from "./modules/audit/pages/AuditCenterPage";
import MasterConfigPage from "./modules/admin/pages/MasterConfigPage";
import CompanyListPage from "./modules/companies/pages/CompanyListPage";
import CompanyDetailPage from "./modules/companies/pages/CompanyDetailPage";
import ProductListPage from "./modules/products/pages/ProductListPage";
import ProductDetailPage from "./modules/products/pages/ProductDetailPage";
import ProjectListPage from "./modules/projects/pages/ProjectListPage";
import ProjectDetailPage from "./modules/projects/pages/ProjectDetailPage";
import MeetingListPage from "./modules/meetings/pages/MeetingListPage";
import MeetingDetailPage from "./modules/meetings/pages/MeetingDetailPage";
import IssueListPage from "./modules/issues/pages/IssueListPage";
import PartnersPage from "./modules/partners/pages/PartnersPage";
import AccountsPage from "./modules/financeAccounts/pages/AccountsPage";
import AccountStatementPage from "./modules/financeAccounts/pages/AccountStatementPage";
import IssueDetailPage from "./modules/issues/pages/IssueDetailPage";
import TaskListPage from "./modules/tasks/pages/TaskListPage";
import TaskDetailPage from "./modules/tasks/pages/TaskDetailPage";
import ActionListPage from "./modules/actions/pages/ActionListPage";
import ActionDetailPage from "./modules/actions/pages/ActionDetailPage";
import NoteListPage from "./modules/notes/pages/NoteListPage";
import NoteDetailPage from "./modules/notes/pages/NoteDetailPage";
import MilestoneListPage from "./modules/milestones/pages/MilestoneListPage";
import MilestoneDetailPage from "./modules/milestones/pages/MilestoneDetailPage";
import IdeaListPage from "./modules/ideas/pages/IdeaListPage";
import IdeaDetailPage from "./modules/ideas/pages/IdeaDetailPage";
import DecisionListPage from "./modules/decisions/pages/DecisionListPage";
import DecisionDetailPage from "./modules/decisions/pages/DecisionDetailPage";
import ContactListPage from "./modules/contacts/pages/ContactListPage";
import ContactDetailPage from "./modules/contacts/pages/ContactDetailPage";
import BudgetListPage from "./modules/budgets/pages/BudgetListPage";
import BudgetDetailPage from "./modules/budgets/pages/BudgetDetailPage";
import ExpenseListPage from "./modules/expenses/pages/ExpenseListPage";
import ExpenseDetailPage from "./modules/expenses/pages/ExpenseDetailPage";
import EstimationListPage from "./modules/costing/pages/EstimationListPage";
import EstimationDetailPage from "./modules/costing/pages/EstimationDetailPage";
import ProposalListPage from "./modules/costing/pages/ProposalListPage";
import ProposalDetailPage from "./modules/costing/pages/ProposalDetailPage";
import MediaVaultPage from "./modules/media/pages/MediaVaultPage";
import MediaDetailPage from "./modules/media/pages/MediaDetailPage";
import VaultListPage from "./modules/vault/pages/VaultListPage";
import VaultDetailPage from "./modules/vault/pages/VaultDetailPage";
import AssetListPage from "./modules/assets/pages/AssetListPage";
import AssetDetailPage from "./modules/assets/pages/AssetDetailPage";
import DashboardPage from "./modules/dashboard/pages/DashboardPage";
import CalendarPage from "./modules/calendar/pages/CalendarPage";
import ReportsPage from "./modules/reports/pages/ReportsPage";
import ReportViewPage from "./modules/reports/pages/ReportViewPage";
import LoginPage from "./modules/auth/pages/LoginPage";
import ProfilePage from "./modules/profile/pages/ProfilePage";
import ChangelogPage from "./modules/changelog/pages/ChangelogPage";
import EnquiriesListPage from "./modules/enquiries/pages/EnquiriesListPage";
import EnquiryDetailPage from "./modules/enquiries/pages/EnquiryDetailPage";
import LeadsListPage from "./modules/leads/pages/LeadsListPage";
import LeadDetailPage from "./modules/leads/pages/LeadDetailPage";
import ApiKeysAdminPage from "./modules/admin/api-keys/pages/ApiKeysAdminPage";
import type { ReactNode } from "react";
import { AuthProvider } from "./context/AuthContext";
import { useAuth } from "./modules/auth/hooks/useAuth";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground font-sans">
      Loading…
    </div>
  );
}

function ProtectedRoute() {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();
  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (user?.mustChangePassword && !location.pathname.startsWith("/profile")) {
    return (
      <Navigate to="/profile?tab=security&forced=true" replace />
    );
  }
  return <Outlet />;
}

// Route-level, deny-by-default enforcement for a permission-bearing module.
// Rendered inside ProtectedRoute, so the user is always authenticated here.
// Super Admin bypasses; everyone else needs canView on the module or is
// redirected to the dashboard (the API independently returns 403).
function RequirePermission({
  module,
  action = "canView",
  children,
}: {
  module: string;
  action?: "canView" | "canAdmin";
  children: ReactNode;
}) {
  const { user } = useAuth();
  const isSuperAdmin = !!user?.roles.some((r) => r.name === "Super Admin");
  const allowed = isSuperAdmin || !!user?.permissions?.[module]?.[action];
  if (!allowed) return <Navigate to="/dashboard" replace />;
  return <>{children}</>;
}

function SuperAdminRoute() {
  const { user, isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  const isSuperAdmin = !!user?.roles.some((r) => r.name === "Super Admin");
  if (!isSuperAdmin) {
    if (typeof window !== "undefined") {
      window.alert("Access restricted to Super Admin");
    }
    return <Navigate to="/dashboard" replace />;
  }
  return <Outlet />;
}

function PublicOnlyRoute() {
  const { isAuthenticated, isLoading } = useAuth();
  if (isLoading) return <FullScreenLoader />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return <Outlet />;
}

function AppRoutes() {
  return (
    <Routes>
      <Route element={<PublicOnlyRoute />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      <Route element={<ProtectedRoute />}>
        <Route path="/" element={<DashboardPage />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route
          path="/calendar"
          element={
            <RequirePermission module="calendar">
              <CalendarPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequirePermission module="user_management">
              <UserListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/users/:id"
          element={
            <RequirePermission module="user_management">
              <UserDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/roles"
          element={
            <RequirePermission module="role_management">
              <RoleListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/roles/new"
          element={
            <RequirePermission module="role_management">
              <CreateRolePage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/roles/:id"
          element={
            <RequirePermission module="role_management">
              <RoleDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/companies"
          element={
            <RequirePermission module="companies">
              <CompanyListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/companies/:id"
          element={
            <RequirePermission module="companies">
              <CompanyDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/products"
          element={
            <RequirePermission module="products">
              <ProductListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/products/:id"
          element={
            <RequirePermission module="products">
              <ProductDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/projects"
          element={
            <RequirePermission module="projects">
              <ProjectListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/projects/:id"
          element={
            <RequirePermission module="projects">
              <ProjectDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/meetings"
          element={
            <RequirePermission module="meetings">
              <MeetingListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/meetings/:id"
          element={
            <RequirePermission module="meetings">
              <MeetingDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/accounts/:id"
          element={
            <RequirePermission module="finance_accounts">
              <AccountStatementPage />
            </RequirePermission>
          }
        />
        <Route
          path="/finance/accounts"
          element={
            <RequirePermission module="finance_accounts">
              <AccountsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/partners"
          element={
            <RequirePermission module="partners">
              <PartnersPage />
            </RequirePermission>
          }
        />
        <Route
          path="/qa/issues"
          element={
            <RequirePermission module="qa_issues">
              <IssueListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/qa/issues/:id"
          element={
            <RequirePermission module="qa_issues">
              <IssueDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/tasks"
          element={
            <RequirePermission module="tasks">
              <TaskListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/tasks/:id"
          element={
            <RequirePermission module="tasks">
              <TaskDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/actions"
          element={
            <RequirePermission module="actions">
              <ActionListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/actions/:id"
          element={
            <RequirePermission module="actions">
              <ActionDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/notes"
          element={
            <RequirePermission module="notes">
              <NoteListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/notes/:id"
          element={
            <RequirePermission module="notes">
              <NoteDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/milestones"
          element={
            <RequirePermission module="milestones">
              <MilestoneListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/milestones/:id"
          element={
            <RequirePermission module="milestones">
              <MilestoneDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ideas"
          element={
            <RequirePermission module="ideas">
              <IdeaListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/ideas/:id"
          element={
            <RequirePermission module="ideas">
              <IdeaDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/decisions"
          element={
            <RequirePermission module="decisions">
              <DecisionListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/decisions/:id"
          element={
            <RequirePermission module="decisions">
              <DecisionDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/contacts"
          element={
            <RequirePermission module="contacts">
              <ContactListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/contacts/:id"
          element={
            <RequirePermission module="contacts">
              <ContactDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/budgets"
          element={
            <RequirePermission module="budgets">
              <BudgetListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/budgets/:id"
          element={
            <RequirePermission module="budgets">
              <BudgetDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/expenses"
          element={
            <RequirePermission module="expenses">
              <ExpenseListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/expenses/:id"
          element={
            <RequirePermission module="expenses">
              <ExpenseDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/estimations"
          element={
            <RequirePermission module="estimations">
              <EstimationListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/estimations/:id"
          element={
            <RequirePermission module="estimations">
              <EstimationDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/proposals"
          element={
            <RequirePermission module="proposals">
              <ProposalListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/proposals/:id"
          element={
            <RequirePermission module="proposals">
              <ProposalDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/media-vault"
          element={
            <RequirePermission module="media">
              <MediaVaultPage />
            </RequirePermission>
          }
        />
        <Route
          path="/media-vault/:id"
          element={
            <RequirePermission module="media">
              <MediaDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/access-vault"
          element={
            <RequirePermission module="access_vault">
              <VaultListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/access-vault/:id"
          element={
            <RequirePermission module="access_vault">
              <VaultDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/assets"
          element={
            <RequirePermission module="assets">
              <AssetListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/assets/:id"
          element={
            <RequirePermission module="assets">
              <AssetDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/enquiries"
          element={
            <RequirePermission module="enquiries">
              <EnquiriesListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/enquiries/:id"
          element={
            <RequirePermission module="enquiries">
              <EnquiryDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/leads"
          element={
            <RequirePermission module="leads">
              <LeadsListPage />
            </RequirePermission>
          }
        />
        <Route
          path="/leads/:id"
          element={
            <RequirePermission module="leads">
              <LeadDetailPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/api-keys"
          element={
            <RequirePermission module="api_keys" action="canAdmin">
              <ApiKeysAdminPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports"
          element={
            <RequirePermission module="reports">
              <ReportsPage />
            </RequirePermission>
          }
        />
        <Route
          path="/reports/:reportId"
          element={
            <RequirePermission module="reports">
              <ReportViewPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/dropdowns"
          element={
            <RequirePermission module="dropdown_manager">
              <DropdownManagerPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RequirePermission module="audit_center">
              <AuditCenterPage />
            </RequirePermission>
          }
        />
        <Route
          path="/admin/changelog"
          element={
            <RequirePermission module="changelog">
              <ChangelogPage />
            </RequirePermission>
          }
        />
        <Route element={<SuperAdminRoute />}>
          <Route path="/admin/modules" element={<ModuleManagerPage />} />
          <Route path="/admin/master-config" element={<MasterConfigPage />} />
        </Route>
      </Route>

      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
